import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { releasesApi, devicesApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  acknowledged: 'bg-blue-100 text-blue-800',
  downloading: 'bg-purple-100 text-purple-800',
  installed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function ReleasesPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  // Upload form state
  const [version, setVersion] = useState('');
  const [platform, setPlatform] = useState('win32');
  const [releaseNotes, setReleaseNotes] = useState('');

  // Push update state
  const [pushReleaseId, setPushReleaseId] = useState('');
  const [pushDeviceId, setPushDeviceId] = useState('');

  const { data: releases = [], isLoading: loadingReleases } = useQuery({
    queryKey: ['releases'],
    queryFn: async () => {
      const resp = await releasesApi.list();
      return resp.data;
    },
  });

  const { data: devices } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const resp = await devicesApi.list();
      return resp.data;
    },
  });

  const { data: commandsData, isLoading: loadingCommands } = useQuery({
    queryKey: ['update-commands'],
    queryFn: async () => {
      const resp = await releasesApi.getUpdateCommands({ limit: 50 });
      return resp.data;
    },
    refetchInterval: 15000,
  });

  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const resp = await releasesApi.upload(formData);
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      setVersion('');
      setReleaseNotes('');
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await releasesApi.deactivate(id);
      return resp.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['releases'] }),
  });

  const pushMutation = useMutation({
    mutationFn: async (data: { releaseId: string; targetDeviceId?: string }) => {
      const resp = await releasesApi.pushUpdate(data);
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['update-commands'] });
      setPushReleaseId('');
      setPushDeviceId('');
    },
  });

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !version) return;

    const formData = new FormData();
    formData.append('binary', file);
    formData.append('version', version);
    formData.append('platform', platform);
    if (releaseNotes) formData.append('releaseNotes', releaseNotes);
    uploadMutation.mutate(formData);
  };

  const handlePush = () => {
    if (!pushReleaseId) return;
    const data: any = { releaseId: pushReleaseId };
    if (pushDeviceId) data.targetDeviceId = pushDeviceId;
    pushMutation.mutate(data);
  };

  const commands = commandsData?.data || [];
  const apiBaseUrl = window.location.origin;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Releases & Updates</h1>
        <p className="text-gray-600">Manage app releases and push updates to devices</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload New Release */}
        <Card>
          <CardHeader>
            <CardTitle>Upload New Release</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-3">
              <Input
                type="text"
                placeholder="Version (e.g. 1.1.0)"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                required
              />
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="win32">Windows (win32)</option>
                <option value="linux">Linux</option>
                <option value="darwin">macOS (darwin)</option>
              </select>
              <textarea
                placeholder="Release notes (optional)"
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={3}
              />
              <input
                ref={fileRef}
                type="file"
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
                required
              />
              <Button
                type="submit"
                className="w-full"
                disabled={uploadMutation.isPending}
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Upload Release'}
              </Button>
              {uploadMutation.isError && (
                <p className="text-sm text-red-600">Upload failed. Please try again.</p>
              )}
              {uploadMutation.isSuccess && (
                <p className="text-sm text-green-600">Release uploaded successfully!</p>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Push Update */}
        <Card>
          <CardHeader>
            <CardTitle>Push Update</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <select
                value={pushReleaseId}
                onChange={(e) => setPushReleaseId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Select Release</option>
                {(releases as any[]).filter((r: any) => r.isActive).map((r: any) => (
                  <option key={r.id} value={r.id}>
                    v{r.version} ({r.platform})
                  </option>
                ))}
              </select>
              <select
                value={pushDeviceId}
                onChange={(e) => setPushDeviceId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">All Devices in Organization</option>
                {(devices || []).map((d: any) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.user?.firstName} {d.user?.lastName})
                  </option>
                ))}
              </select>
              <Button
                className="w-full"
                onClick={handlePush}
                disabled={!pushReleaseId || pushMutation.isPending}
              >
                {pushMutation.isPending
                  ? 'Pushing...'
                  : pushDeviceId
                  ? 'Push to Device'
                  : 'Push to All Devices'}
              </Button>
              {pushMutation.isError && (
                <p className="text-sm text-red-600">Push failed. Please try again.</p>
              )}
              {pushMutation.isSuccess && (
                <p className="text-sm text-green-600">Update pushed successfully!</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Download Links */}
      <Card>
        <CardHeader>
          <CardTitle>Download Links</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-3">
            Share these links with employees to install Apace Pulse. Links always point to the latest active version.
          </p>
          <div className="space-y-2">
            {['win32', 'linux', 'darwin'].map((p) => (
              <div key={p} className="flex items-center gap-2">
                <span className="text-sm font-medium w-20">
                  {p === 'win32' ? 'Windows' : p === 'darwin' ? 'macOS' : 'Linux'}:
                </span>
                <code className="flex-1 text-xs bg-gray-100 px-3 py-1.5 rounded font-mono truncate">
                  {apiBaseUrl}/api/releases/download/{p}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(`${apiBaseUrl}/api/releases/download/${p}`)}
                >
                  Copy
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Releases Table */}
      <Card>
        <CardHeader>
          <CardTitle>Releases</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingReleases ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : (releases as any[]).length === 0 ? (
            <div className="p-8 text-center text-gray-500">No releases uploaded yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Version</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Platform</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Size</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Active</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(releases as any[]).map((r: any) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">v{r.version}</td>
                      <td className="px-4 py-2 text-gray-600">{r.platform}</td>
                      <td className="px-4 py-2 text-gray-600">{formatBytes(Number(r.fileSize))}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${r.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {r.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deactivateMutation.mutate(r.id)}
                            disabled={deactivateMutation.isPending}
                          >
                            Deactivate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Commands */}
      <Card>
        <CardHeader>
          <CardTitle>Update Commands</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingCommands ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : commands.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No update commands yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Device</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Target Version</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {commands.map((cmd: any) => (
                    <tr key={cmd.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs">{cmd.targetDeviceId || 'All'}</td>
                      <td className="px-4 py-2 font-medium">v{cmd.release?.version}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[cmd.status] || 'bg-gray-100 text-gray-800'}`}>
                          {cmd.status}
                        </span>
                        {cmd.error && (
                          <span className="ml-2 text-xs text-red-500">{cmd.error}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">
                        {new Date(cmd.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">
                        {new Date(cmd.updatedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
